import lodashGet from "lodash/get";
import { formatMessage } from "lib/globalize-util";
import { distributeEvenly, formatThousands, safeParseFloat } from "lib/number";
import { Item } from "constants/product-collection";
import {
    calculateAvgPPS,
    checkRequiredDesired,
    genericParams,
    getCellRendererSelector,
    ISchemaKey,
    schemaToGrid,
} from "helpers/product-collection";
import {
    IAllocationDimensionValue,
    AllocationBagType,
    getAllocationBagValue,
    setBagValue,
    AllocationBag,
} from "helpers/allocation-dimension";
import { tableValueGetter, getTableValueSetter, saveToTable } from "helpers/table-bag";
import {
    IDoor,
    legacy_parseDimensionParams,
    getDoorsGroupColumns,
    getDimensionFilters,
    extendColDefWithSizes,
    wrapChildrenWithNoSiblings,
    getAttributeFilters,
    filterDatesOrDoors,
} from "helpers/door-collection";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { IGridParamsQueryParam } from "../tool-panels";
import { ColDef, ColGroupDef, GridApi, ValueFormatterParams } from "ag-grid-community";
import { IsColumnFuncParams, ValueGetterParams, ValueSetterParams } from "ag-grid-community/dist/lib/entities/colDef";
import { expandValueArray } from "helpers/sizing";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { ICurrency } from "../grid-renderers/pinned-row-renderers";
import { UNITS } from "helpers/metrics/formulas/formula-units";
import { MetricsKind } from "helpers/metrics/types";
export const BULK_IDENTIFIER = "BULK";
export const SIZE_TOTALS_IDENTIFIER = "SIZE_TOTALS";
export const COMPARISON_TOTALS_IDENTIFIER = "COMPARISON_TOTALS";
export const DIFFERENCE_IDENTIFIER = "DIFF_ROW";
export const SIZE_IDENTIFIER = "SIZE";
export const DEFAULT_VALUES_PINNED_ROW = "DEFAULTS_ROW";

export const numberParser = (params) => {
    return Number(params.newValue);
};

export const isEditable = ({ node }) => {
    return !node.rowPinned;
};

export enum AllocationGridColumns {
    Size = "size",
    CostVendorPPS = "cost_vendor_pps",
    RetailVendorPPS = "retail_vendor_pps",
    UPC = "upc",
    Totals = "totals",
    NRFSizeCode = "nrf_size_codes",
    NRFSizeDescription = "nrf_size_descriptions",
}

export const detailColumnValueGetterCreator = (creatorParams) => {
    const { breadcrumb, isTotalColumn, sizes } = creatorParams;
    const sizesEnabled = !!sizes;
    const hasSizes = sizesEnabled && sizes?.length;
    return (getterParams) => {
        const { data, api, column, context } = getterParams;
        const itemId = data.rowId;

        const allocationBag = context?.allocationBagRef?.allocationBag;
        if (allocationBag) {
            if (data.type === SIZE_TOTALS_IDENTIFIER) {
                let bulkTotal = 0;
                let sizeTotal = 0;
                api.forEachNode((rowNode) => {
                    if (!sizesEnabled) {
                        if (rowNode.data.type === BULK_IDENTIFIER) {
                            bulkTotal += api.getValue(column, rowNode) || 0;
                        } else {
                            sizeTotal += api.getValue(column, rowNode) || 0;
                        }
                        return;
                    }
                    if (rowNode.data.type === SIZE_IDENTIFIER) {
                        bulkTotal += breadcrumb.reduce(
                            (acc, col) =>
                                acc + (getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col) || 0),
                            0
                        );
                        if (hasSizes) {
                            sizeTotal += breadcrumb.reduce((acc, col) => {
                                const sizeValue = sizes.reduce(
                                    (acc2, size) =>
                                        acc2 +
                                        (getAllocationBagValue(
                                            AllocationBagType.allocations,
                                            allocationBag,
                                            itemId,
                                            col.concat(size)
                                        ) || 0),
                                    0
                                );
                                return acc + sizeValue;
                            }, 0);
                        }
                    }
                });
                return `${sizeTotal} / ${bulkTotal}`;
            }
            if (isTotalColumn) {
                return breadcrumb.reduce((acc, col) => {
                    let quantity = 0;
                    if (data.type === BULK_IDENTIFIER) {
                        quantity = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col);
                    } else {
                        if (hasSizes) {
                            quantity = sizes.reduce((total, size) => {
                                const value = getAllocationBagValue(
                                    AllocationBagType.allocations,
                                    allocationBag,
                                    itemId,
                                    col.concat([size])
                                );
                                return total + (value ?? 0);
                            }, 0);
                        } else if (sizesEnabled) {
                            quantity = getAllocationBagValue(AllocationBagType.bulks, allocationBag, itemId, col);
                        } else {
                            quantity = getAllocationBagValue(
                                AllocationBagType.allocations,
                                allocationBag,
                                itemId,
                                col.concat([data.size])
                            );
                        }
                    }
                    return quantity ? quantity + (acc || 0) : acc;
                }, undefined);
            }
            const type =
                data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                    ? AllocationBagType.bulks
                    : AllocationBagType.allocations;
            const path =
                data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                    ? (breadcrumb as string[])
                    : (breadcrumb as string[]).concat([data.size]);
            return getAllocationBagValue(type, allocationBag, itemId, path);
        }

        if (data.type === SIZE_TOTALS_IDENTIFIER) {
            let totalBulks = 0;
            let totalQuantity = 0;
            api.forEachNode((rowNode) => {
                if (sizesEnabled) {
                    if (rowNode.data.type === SIZE_IDENTIFIER) {
                        const nodeData = rowNode.data;
                        const allocationsRef = lodashGet(nodeData, Item.ALLOCATIONS);
                        const bulksRef = lodashGet(nodeData, Item.BULKS);
                        totalBulks += (breadcrumb as string[][]).reduce(
                            (acc, col) => acc + (tableValueGetter(bulksRef.current, col) || 0),
                            0
                        );
                        totalQuantity += hasSizes
                            ? (breadcrumb as string[][]).reduce(
                                  (acc, col) =>
                                      acc +
                                      sizes.reduce(
                                          (acc2, size) =>
                                              acc2 + (tableValueGetter(allocationsRef.current, col.concat(size)) || 0),
                                          0
                                      ),
                                  0
                              )
                            : 0;
                    }
                } else {
                    if (rowNode.data.type === BULK_IDENTIFIER) {
                        totalBulks += api.getValue(column, rowNode) || 0;
                    } else {
                        totalQuantity += api.getValue(column, rowNode) || 0;
                    }
                }
            });
            return sizesEnabled ? `${totalQuantity}` : `${totalQuantity} / ${totalBulks}`;
        } else if (isTotalColumn) {
            return (breadcrumb as string[][]).reduce<number | undefined>((acc, col) => {
                let quantity = 0;
                const bulksRef = lodashGet(data, Item.BULKS);
                if (data.type === BULK_IDENTIFIER) {
                    quantity = bulksRef.current ? tableValueGetter(bulksRef.current, col) : undefined;
                } else {
                    const allocationsRef = lodashGet(data, Item.ALLOCATIONS);
                    if (hasSizes) {
                        quantity = sizes.reduce((total, size) => {
                            const value = tableValueGetter(allocationsRef.current, col.concat([size]));
                            return total + (value ?? 0);
                        }, 0);
                    } else if (sizesEnabled) {
                        quantity = tableValueGetter(bulksRef.current, col);
                    } else {
                        quantity = allocationsRef.current
                            ? tableValueGetter(allocationsRef.current, col.concat([data.size]))
                            : undefined;
                    }
                }
                return quantity ? (acc || 0) + quantity : acc;
            }, undefined);
        }

        const table =
            data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                ? lodashGet(data, Item.BULKS).current
                : lodashGet(data, Item.ALLOCATIONS).current;
        const tablePaths =
            data.type === BULK_IDENTIFIER || (sizesEnabled && !hasSizes)
                ? (breadcrumb as string[])
                : (breadcrumb as string[]).concat([data.size]);
        return tableValueGetter(table, tablePaths);
    };
};

export const detailColumnValueSetterCreator = (creatorParams) => {
    const { breadcrumb, isTotalColumn, sizes, incrementLock, hasAllocationBag } = creatorParams;
    const sizesEnabled = !!sizes;
    const sizesEnabledButEmpty = sizes && sizes.length === 0;

    if (hasAllocationBag) {
        if (isTotalColumn) {
            return (params) => {
                const { data, oldValue, newValue, context } = params;
                const { onMutateBagValues } = context;

                if (oldValue === newValue) {
                    return false;
                }

                const itemId = data.rowId;
                let mutatedAllocationBag = context.allocationBagRef?.allocationBag;
                const distribution = distributeEvenly(newValue, breadcrumb.length);
                distribution.forEach((quantity, index) => {
                    const singleBreadcrumb = breadcrumb[index];
                    onMutateBagValues({
                        quantity,
                        dimensionValues: singleBreadcrumb,
                        rowId: itemId,
                        bagType: AllocationBagType.bulks,
                    });
                    mutatedAllocationBag = setBagValue(
                        AllocationBagType.bulks,
                        mutatedAllocationBag,
                        itemId,
                        singleBreadcrumb,
                        quantity
                    );
                });

                context.allocationBagRef.allocationBag = mutatedAllocationBag;
                return true;
            };
        }
        return (params) => {
            const { oldValue, newValue, context, data: rowData } = params;
            const { onMutateBagValues } = context;
            if (oldValue === newValue) {
                return false;
            }

            const itemId = rowData.rowId;

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                onMutateBagValues({
                    quantity: newValue,
                    dimensionValues: breadcrumb,
                    rowId: itemId,
                    bagType: AllocationBagType.bulks,
                });
                context.allocationBagRef.allocationBag = setBagValue(
                    AllocationBagType.bulks,
                    context.allocationBagRef.allocationBag,
                    itemId,
                    breadcrumb,
                    newValue
                );
            } else {
                let dimensionValues = [...breadcrumb];
                // Sizing grid with `sizes` (eg: GOA merchant) is different. Size is already included in breadcrumb.
                if (!sizesEnabled) {
                    dimensionValues = dimensionValues.concat([rowData.size]);
                }
                onMutateBagValues({
                    quantity: newValue,
                    dimensionValues,
                    rowId: itemId,
                    bagType: AllocationBagType.allocations,
                });
                context.allocationBagRef.allocationBag = setBagValue(
                    AllocationBagType.allocations,
                    context.allocationBagRef.allocationBag,
                    itemId,
                    dimensionValues,
                    newValue
                );
            }

            return true;
        };
    }

    if (isTotalColumn) {
        return (setterParams) => {
            incrementLock?.();

            const { data: rowData, newValue } = setterParams;
            if (rowData.type !== BULK_IDENTIFIER) {
                return false;
            }
            const distribution = distributeEvenly(newValue, breadcrumb.length);
            const newTable = saveToTable(lodashGet(rowData, Item.BULKS).current, breadcrumb, distribution);
            lodashGet(rowData, Item.BULKS).current = newTable;
            return true;
        };
    }
    return getTableValueSetter(
        (newTable, setterParams) => {
            const { data: rowData } = setterParams;

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                lodashGet(rowData, Item.BULKS).current = newTable;
            } else {
                lodashGet(rowData, Item.ALLOCATIONS).current = newTable;
            }
        },
        ({ data: rowData }) => {
            incrementLock?.();

            if (rowData.type === BULK_IDENTIFIER || sizesEnabledButEmpty) {
                return lodashGet(rowData, Item.BULKS).current;
            } else {
                return lodashGet(rowData, Item.ALLOCATIONS).current;
            }
        },
        ({ data: rowData }) => {
            if (rowData.type === BULK_IDENTIFIER) {
                return breadcrumb as string[];
            } else {
                return (breadcrumb as string[]).concat(rowData.size ? [rowData.size] : []);
            }
        }
    );
};

const isSizeLevelCellEditable = ({ data }: IsColumnFuncParams) =>
    data?.type === SIZE_IDENTIFIER && data?.vendorSizeIndex >= 0 && data.sizeLevelFieldsEditable;
const sizeLevelFieldValueSetter = ({ colDef, data, newValue }: ValueSetterParams) => {
    if (data && data.type === SIZE_IDENTIFIER) {
        const sizeLevelInfo = data.SizeLevelFields.current;
        if (sizeLevelInfo) {
            const valueArray = sizeLevelInfo[colDef.field];
            data.SizeLevelFields.current[colDef.field] = expandValueArray(
                valueArray,
                data.vendorSizeIndex,
                newValue ?? ""
            );
            return true;
        }
    }
};
const sizeLevelFieldValueGetter = ({ data, colDef }: ValueGetterParams) => {
    const vendorSizeIndex = data?.vendorSizeIndex;
    if (vendorSizeIndex || vendorSizeIndex === 0) {
        const sizeLevelInfo = data?.SizeLevelFields.current;
        if (sizeLevelInfo) {
            return sizeLevelInfo[colDef.field][vendorSizeIndex];
        }
    }
};

export const pricePerSizeValueGetter = (columnType: AllocationGridColumns, currency?: ICurrency) => {
    return (params: ValueGetterParams) => {
        const { data } = params;
        const { size, type } = data;
        const pricePerSizeMap = lodashGet(data, `fields.${columnType}.struct_value`, {});

        if (type === BULK_IDENTIFIER) {
            return calculateAvgPPS(data, columnType, currency?.currency_code);
        }

        const pricePerSizeMapKeys = Object.keys(pricePerSizeMap);
        for (const key of pricePerSizeMapKeys) {
            const priceString = pricePerSizeMap[key];
            const [sizeString, currencyCode] = key.split("#");
            if (sizeString === size && currencyCode === currency?.currency_code) {
                return safeParseFloat(priceString, undefined);
            }
        }
    };
};

export const pricePerSizeFormatter = (currency?: ICurrency) => {
    return (params: ValueFormatterParams) => {
        if (params.value && !isNaN(params.value)) {
            return (currency?.symbol || "$") + formatThousands(params.value.toFixed(currency?.decimal_places || 2));
        }

        return undefined;
    };
};

export const sizeTotalsValueGetter = (
    assortmentDimensionMetrics: boolean | undefined,
    doors: IDoor[],
    dimensionFilters: string[][],
    globalGroups: IAllocationDimensionValue[]
) => {
    return (params: ValueGetterParams) => {
        const { data, node, api, columnApi } = params;
        if (data.type === BULK_IDENTIFIER) {
            return formatMessage("Bulk");
        }

        let totalColumns = [];
        if (!assortmentDimensionMetrics) {
            const columns = columnApi.getAllColumns();
            totalColumns = columns.some((column) => column.getColDef().type === ColumnTypes.AllocationDimensionTotal)
                ? columns.filter((column) => column.getColDef().type === ColumnTypes.AllocationDimensionTotal)
                : columns.filter((column) => column.getColDef().type === ColumnTypes.DoorBulkQuantity);
        }

        if (data.type === SIZE_TOTALS_IDENTIFIER) {
            let totalBulks = 0;
            let totalQuantity = 0;

            if (assortmentDimensionMetrics) {
                const filteredDoors = filterDatesOrDoors(doors, "doors", dimensionFilters, globalGroups?.length > 0);
                totalBulks =
                    UNITS({
                        factoryOptions: {
                            metric: {
                                showValue: false,
                                showTotals: false,
                                showSubtotals: false,
                                kind: MetricsKind.UNITS,
                                displayName: "",
                            },
                            doors: filteredDoors,
                            deliveryId: null,
                        },
                        getterParams: params,
                    }) || 0;

                totalQuantity =
                    UNITS({
                        factoryOptions: {
                            metric: {
                                showValue: false,
                                showTotals: false,
                                showSubtotals: false,
                                kind: MetricsKind.UNITS,
                                displayName: "",
                            },
                            doors: filteredDoors,
                            deliveryId: null,
                            useSize: true,
                            useAllSizes: true,
                        },
                        getterParams: params,
                    }) || 0;
            } else {
                api.forEachNode((rowNode) => {
                    totalColumns.forEach((totalColumn) => {
                        if (rowNode.data.type === BULK_IDENTIFIER) {
                            totalBulks += api.getValue(totalColumn, rowNode) || 0;
                        } else {
                            totalQuantity += api.getValue(totalColumn, rowNode) || 0;
                        }
                    });
                });
            }

            return `${totalQuantity} / ${totalBulks}`;
        }

        let total = 0;
        const { size } = data;
        if (assortmentDimensionMetrics && size) {
            const filteredDoors = filterDatesOrDoors(doors, "doors", dimensionFilters, globalGroups?.length > 0);
            total =
                UNITS({
                    factoryOptions: {
                        metric: {
                            showValue: false,
                            showTotals: false,
                            showSubtotals: false,
                            kind: MetricsKind.UNITS,
                            displayName: "",
                        },
                        doors: filteredDoors,
                        deliveryId: null,
                        useSize: true,
                    },
                    getterParams: params,
                }) || 0;
        } else {
            totalColumns.forEach((column) => {
                const value = api.getValue(column, node);
                total += typeof value === "number" ? value : 0;
            });
        }
        return total;
    };
};

export const detailColumnDef = (
    doors: IDoor[] = [],
    dates: IAllocationDimensionValue[] = [],
    doorClassificationSchema: ISchemaKey[] = [],
    dimensionsPanelApplied: DimensionsPanelReduxState,
    readOnly: boolean = false,
    gridParams: IGridParamsQueryParam,
    currentAllocationDimensionView = [],
    isMobile = false,
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[],
    allocationBagRef?: {
        allocationBag: AllocationBag;
    },
    sizes?: string[],
    schemaElements?: ISchemaKey[],
    rootGridApi?: GridApi,
    incrementLock?: () => void,
    assortmentDimensionMetrics?: boolean,
    enablePricePerSize?: boolean,
    merchantCurrency?: ICurrency
): (ColDef | ColGroupDef)[] => {
    const { attributeHiddenValues, attributeDirection } = legacy_parseDimensionParams(dimensionsPanelApplied);
    const dimensionFilters = getDimensionFilters(gridParams);
    const attributeFilters = getAttributeFilters(gridParams);
    const globalGroups = dates || [];
    const sizesEnabled = !!sizes;
    const hasSizes = sizesEnabled && sizes.length;
    const doorColumns = getDoorsGroupColumns({
        globalGroups,
        currentLayout: currentAllocationDimensionView,
        doors,
        doorClassificationSchema,
        collapsedGroupIds: dimensionsPanelApplied.collapsedGroupIds,
        hiddenGroupIds: attributeHiddenValues,
        groupDirection: attributeDirection,
        merchantPurchaseOrderDoorGroups,
        dimensionsPanelApplied,
        allocationBagRef,
        gridParams,
        editable:
            !sizesEnabled &&
            (!readOnly
                ? (params) => {
                      const { data, context } = params;
                      // Only editable if size and bulk
                      return [...(context.sizePacksApplicable ? [] : [SIZE_IDENTIFIER]), BULK_IDENTIFIER].includes(
                          data.type
                      );
                  }
                : false),
        totalEditable:
            !sizesEnabled &&
            (!readOnly
                ? ({ data }) => {
                      // Only allow totals bulks to be editable
                      return data.type === BULK_IDENTIFIER;
                  }
                : false),
        dimensionFilters,
        attributeFilters,
        valueGetterCreator: (params) => detailColumnValueGetterCreator({ ...params, sizes: sizes }),
        valueSetterCreator: (params) =>
            detailColumnValueSetterCreator({
                ...params,
                sizes: sizes,
                incrementLock,
                hasAllocationBag: Boolean(allocationBagRef),
            }),
        cellClass: ({ data: rowData }) => {
            if (rowData.type === BULK_IDENTIFIER) {
                return ["bulksCell"];
            }
            return [];
        },
        movable: false,
        assortmentDimensionMetrics,
        useSize: true,
        merchantCurrency,
        rootGridApi,
    });
    let doorColumnsWithSizes = doorColumns;
    if (hasSizes) {
        const containerNode = { children: doorColumns };
        extendColDefWithSizes(containerNode, sizes, Boolean(allocationBagRef));
        wrapChildrenWithNoSiblings(containerNode);
        doorColumnsWithSizes = containerNode.children;
    }
    if (sizesEnabled) {
        const [totalCol, ...remainingCols] = doorColumnsWithSizes;
        const totalColAsColGroupDef = totalCol as ColGroupDef;
        const message = formatMessage("Total Units");
        if (totalColAsColGroupDef.children) {
            totalColAsColGroupDef.children[0] = {
                ...totalColAsColGroupDef.children[0],
                headerName: message,
                pinned: true,
            };
        }
        doorColumnsWithSizes = [{ ...totalCol, headerName: message, pinned: true }, ...remainingCols];
    }

    const pricePerSizeColumns = enablePricePerSize
        ? [
              {
                  field: AllocationGridColumns.CostVendorPPS,
                  headerName: formatMessage("Cost Price"),
                  editable: false,
                  width: 90,
                  pinned: "left",
                  cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                  // use getCellRendererParamsFn if you use more than one cell renderers
                  cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                  cellClass: "pinned-cell size-col",
                  lockPosition: true,
                  suppressMenu: true,
                  suppressMovable: true,
                  lockPinned: true,
                  filter: false,
                  valueGetter: pricePerSizeValueGetter(AllocationGridColumns.CostVendorPPS, merchantCurrency),
                  valueFormatter: pricePerSizeFormatter(merchantCurrency),
              },
              {
                  field: AllocationGridColumns.RetailVendorPPS,
                  headerName: formatMessage("Retail Price"),
                  editable: false,
                  width: 90,
                  pinned: "left",
                  cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                  // use getCellRendererParamsFn if you use more than one cell renderers
                  cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                  cellClass: "pinned-cell size-col",
                  lockPosition: true,
                  suppressMenu: true,
                  suppressMovable: true,
                  lockPinned: true,
                  filter: false,
                  valueGetter: pricePerSizeValueGetter(AllocationGridColumns.RetailVendorPPS, merchantCurrency),
                  valueFormatter: pricePerSizeFormatter(merchantCurrency),
              },
          ]
        : [];

    const options = schemaElements ? { rowLevelConstraint: checkRequiredDesired(schemaElements) } : undefined;
    return sizesEnabled
        ? doorColumnsWithSizes
        : [
              {
                  field: AllocationGridColumns.Size,
                  headerName: formatMessage("Size"),
                  chartDataType: "category",
                  editable: false,
                  width: 90,
                  pinned: "left",
                  cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                  // use getCellRendererParamsFn if you use more than one cell renderers
                  cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                  cellClass: "pinned-cell size-col",
                  lockPosition: true,
                  suppressMenu: true,
                  suppressMovable: true,
                  lockPinned: true,
                  filter: false,
              },
              ...pricePerSizeColumns,
              {
                  field: AllocationGridColumns.Totals,
                  headerName: formatMessage("Size Totals"),
                  editable: false,
                  width: 90,
                  pinned: "left",
                  cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                  // use getCellRendererParamsFn if you use more than one cell renderers
                  cellRendererParams: {
                      style: { textAlign: "right", backgroundColor: "#f5f7f7", height: "100%", width: "100%" },
                      splitValue: true,
                  },
                  cellClass: "pinned-cell totals",
                  lockPosition: true,
                  suppressMenu: true,
                  suppressMovable: true,
                  lockPinned: true,
                  filter: false,
                  hide: isMobile,
                  valueParser: numberParser,
                  valueGetter: sizeTotalsValueGetter(assortmentDimensionMetrics, doors, dimensionFilters, globalGroups),
              },
              {
                  headerName: formatMessage("Details"),
                  marryChildren: true,
                  children: [
                      {
                          field: AllocationGridColumns.UPC,
                          headerName: formatMessage("UPC"),
                          editable: isSizeLevelCellEditable,
                          width: 90,
                          cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                          // use getCellRendererParamsFn if you use more than one cell renderers
                          cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                          lockPosition: true,
                          suppressMenu: true,
                          suppressMovable: true,
                          filter: false,
                          hide: isMobile,
                          ...(sizesEnabled
                              ? {}
                              : {
                                    valueGetter: sizeLevelFieldValueGetter,
                                    valueSetter: sizeLevelFieldValueSetter,
                                }),
                      },
                      {
                          field: AllocationGridColumns.NRFSizeCode,
                          headerName: formatMessage("NRF Size Code"),
                          editable: isSizeLevelCellEditable,
                          width: 90,
                          cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                          // use getCellRendererParamsFn if you use more than one cell renderers
                          cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                          lockPosition: true,
                          suppressMenu: true,
                          suppressMovable: true,
                          filter: false,
                          hide: isMobile,
                          columnGroupShow: "open",
                          ...(sizesEnabled
                              ? {}
                              : {
                                    valueGetter: sizeLevelFieldValueGetter,
                                    valueSetter: sizeLevelFieldValueSetter,
                                }),
                      },
                      {
                          field: AllocationGridColumns.NRFSizeDescription,
                          headerName: formatMessage("NRF Size Description"),
                          editable: isSizeLevelCellEditable,
                          width: 90,
                          cellRendererSelector: getCellRendererSelector(null, "customPinnedRowRenderer"),
                          // use getCellRendererParamsFn if you use more than one cell renderers
                          cellRendererParams: { style: { backgroundColor: "#f5f7f7", height: "100%", width: "100%" } },
                          lockPosition: true,
                          suppressMenu: true,
                          suppressMovable: true,
                          filter: false,
                          hide: isMobile,
                          columnGroupShow: "open",
                          ...(sizesEnabled
                              ? {}
                              : {
                                    valueGetter: sizeLevelFieldValueGetter,
                                    valueSetter: sizeLevelFieldValueSetter,
                                }),
                      },
                  ],
              },
              ...(schemaElements
                  ? schemaElements.map((schemaKey) => ({
                        ...schemaToGrid(
                            { schemaKey, params: { ...genericParams, openDetailModal: undefined } },
                            options
                        ),
                        hide: false,
                        suppressMenu: true,
                    }))
                  : []),
              ...doorColumns,
          ];
};
